/**
 * Offline persistence for the POS queue (browser only).
 *
 * WHY INDEXEDDB AND NOT LOCALSTORAGE
 * ----------------------------------
 * A queued till transaction is not a small flag: it carries the invoice lines,
 * the receipt, the projected stock/balance deltas and the printable payload.
 * localStorage is synchronous (it blocks the main thread on every write),
 * capped at ~5 MB and string-only. IndexedDB gives us kilobytes per record, an
 * async API that never janks the cashier's typing, and a stable "one record
 * per key" store that a retry loop can update in place.
 *
 * The database is deliberately tiny and self-contained:
 *
 *   meta      - key/value bookkeeping (snapshot timestamps, schema hints).
 *   outbox    - one record per till transaction that still has to reach the
 *               server, ordered by `capturedAt` (the order the cashier rang
 *               them up). Indexed so a sync run can read them in FIFO order.
 *   documents - printable payloads (bill/receipt) keyed by the local
 *               reference. The print route is a full page load, so it cannot
 *               inherit React state: it re-reads the document from here.
 *
 * Everything degrades to a rejected promise when IndexedDB is unavailable
 * (private mode, ancient browsers, Jest/Node), and every caller in the offline
 * layer is expected to catch that and tell the operator instead of guessing.
 */

const DB_NAME = 'dira360-offline';
/**
 * 2 - added the `cache` store (last-known-good GET responses).
 *
 * The upgrade handler is written as a series of `contains()` checks rather than
 * a `switch (oldVersion)`, so a till that skipped a release (or a brand-new one
 * at version 0) creates exactly the stores it is missing.
 */
const DB_VERSION = 2;

/** Store names, exported so callers never hard-code strings. */
export const OFFLINE_STORES = Object.freeze({
  META: 'meta',
  OUTBOX: 'outbox',
  DOCUMENTS: 'documents',
  CACHE: 'cache'
});

/** Index names inside the cache store. */
export const OFFLINE_CACHE_INDEXES = Object.freeze({
  BY_SAVED_AT: 'by-savedAt'
});

/** Index names inside the outbox store. */
export const OFFLINE_OUTBOX_INDEXES = Object.freeze({
  BY_STATE: 'by-state',
  BY_CAPTURED_AT: 'by-capturedAt'
});

let dbPromise = null;

export const isOfflineDbSupported = () => {
  try {
    return typeof window !== 'undefined' && !!window.indexedDB;
  } catch (_) {
    return false;
  }
};

const offlineDbError = (message) => {
  const error = new Error(message);
  error.code = 'OFFLINE_DB_UNAVAILABLE';
  return error;
};

/**
 * Open (and if needed create) the offline database.
 *
 * The connection is memoised: IndexedDB `open` is expensive and every queue
 * write during a busy service would otherwise pay for it. A lost connection
 * (version change from another tab) drops the cached promise so the next call
 * reconnects instead of failing forever.
 */
export function openOfflineDb() {
  if (!isOfflineDbSupported()) {
    return Promise.reject(offlineDbError('Offline storage is not available in this browser.'));
  }
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    let request;
    try {
      request = window.indexedDB.open(DB_NAME, DB_VERSION);
    } catch (error) {
      dbPromise = null;
      reject(offlineDbError(error?.message || 'Unable to open offline storage.'));
      return;
    }

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(OFFLINE_STORES.META)) {
        db.createObjectStore(OFFLINE_STORES.META, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(OFFLINE_STORES.OUTBOX)) {
        const outbox = db.createObjectStore(OFFLINE_STORES.OUTBOX, { keyPath: 'id' });
        outbox.createIndex(OFFLINE_OUTBOX_INDEXES.BY_STATE, 'state', { unique: false });
        outbox.createIndex(OFFLINE_OUTBOX_INDEXES.BY_CAPTURED_AT, 'capturedAt', { unique: false });
      }
      if (!db.objectStoreNames.contains(OFFLINE_STORES.DOCUMENTS)) {
        db.createObjectStore(OFFLINE_STORES.DOCUMENTS, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(OFFLINE_STORES.CACHE)) {
        const cache = db.createObjectStore(OFFLINE_STORES.CACHE, { keyPath: 'key' });
        // Ordered ascending, so pruning can walk the oldest entries first.
        cache.createIndex(OFFLINE_CACHE_INDEXES.BY_SAVED_AT, 'savedAtMs', { unique: false });
      }
    };

    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => {
        try { db.close(); } catch (_) { /* nothing left to close */ }
        dbPromise = null;
      };
      resolve(db);
    };

    request.onerror = () => {
      dbPromise = null;
      reject(offlineDbError(request.error?.message || 'Unable to open offline storage.'));
    };

    request.onblocked = () => {
      // Another tab still holds an older connection. Tell the caller instead of
      // hanging: the cashier can reload the till and the queue survives.
      dbPromise = null;
      reject(offlineDbError('Offline storage is locked by another tab. Reload the till and try again.'));
    };
  });

  return dbPromise;
}

/**
 * Run `work(store)` inside one transaction and resolve with the transaction's
 * result once it commits.
 *
 * Resolving on `oncomplete` (not on the request's `onsuccess`) is what makes a
 * queued sale durable before the UI reports "saved": a receipt may only claim
 * to be stored once the bytes are actually on disk.
 */
export async function withOfflineStore(storeName, mode, work) {
  const db = await openOfflineDb();
  return new Promise((resolve, reject) => {
    let transaction;
    try {
      transaction = db.transaction(storeName, mode);
    } catch (error) {
      reject(error);
      return;
    }

    let result;
    transaction.oncomplete = () => resolve(result);
    transaction.onerror = () => reject(transaction.error || new Error('Offline storage operation failed.'));
    transaction.onabort = () => reject(transaction.error || new Error('Offline storage operation was aborted.'));

    try {
      const store = transaction.objectStore(storeName);
      const outcome = work(store);
      if (outcome && typeof outcome.then === 'function') {
        outcome.then((value) => { result = value; }).catch((error) => {
          try { transaction.abort(); } catch (_) { /* already finished */ }
          reject(error);
        });
      } else {
        result = outcome;
      }
    } catch (error) {
      try { transaction.abort(); } catch (_) { /* already finished */ }
      reject(error);
    }
  });
}

/** Wrap a single IDBRequest so it can be awaited. */
export const requestToPromise = (request) => new Promise((resolve, reject) => {
  if (!request) {
    resolve(undefined);
    return;
  }
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error || new Error('Offline storage request failed.'));
});

/** Read one record by key (resolves `undefined` when absent). */
export async function offlineGet(storeName, key) {
  return withOfflineStore(storeName, 'readonly', (store) => requestToPromise(store.get(key)));
}

/** Read every record in a store. */
export async function offlineGetAll(storeName) {
  return withOfflineStore(storeName, 'readonly', (store) => requestToPromise(store.getAll()));
}

/** Read every record whose `state` index entry matches. */
export async function offlineGetAllByState(storeName, state) {
  return withOfflineStore(storeName, 'readonly', (store) => (
    requestToPromise(store.index(OFFLINE_OUTBOX_INDEXES.BY_STATE).getAll(state))
  ));
}

/** Insert or replace a record. */
export async function offlinePut(storeName, value) {
  return withOfflineStore(storeName, 'readwrite', (store) => requestToPromise(store.put(value)));
}

/** Delete one record by key. */
export async function offlineDelete(storeName, key) {
  return withOfflineStore(storeName, 'readwrite', (store) => requestToPromise(store.delete(key)));
}

/** Empty a store (used when a user signs out or the tenant changes). */
export async function offlineClear(storeName) {
  return withOfflineStore(storeName, 'readwrite', (store) => requestToPromise(store.clear()));
}

/** Convenience: read a `meta` value with a default. */
export async function readOfflineMeta(key, fallback = null) {
  const record = await offlineGet(OFFLINE_STORES.META, key);
  return record && Object.prototype.hasOwnProperty.call(record, 'value') ? record.value : fallback;
}

/** Convenience: write a `meta` value (JSON-serialisable). */
export async function writeOfflineMeta(key, value) {
  return offlinePut(OFFLINE_STORES.META, { key, value, updatedAt: new Date().toISOString() });
}

/**
 * Close the memoised connection.
 *
 * Needed before the database is deleted (sign-out with "clear local data", and
 * tests): IndexedDB blocks a delete while a connection is open, so dropping the
 * memoised promise alone would leave the old connection alive and the delete
 * pending forever.
 */
export async function closeOfflineDb() {
  const pending = dbPromise;
  dbPromise = null;
  if (!pending) return;
  try {
    const db = await pending;
    if (db && typeof db.close === 'function') db.close();
  } catch (_) {
    // Never opened (or already failed): nothing to close.
  }
}

/** Test/dev hook: forget the memoised connection. */
export function resetOfflineDbConnection() {
  dbPromise = null;
}
